import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControl, InputLabel, Select, MenuItem, TextField, Accordion, AccordionSummary, AccordionDetails, Typography } from '@mui/material';

import { Peak } from './types';
import { primaryPillButtonSx, destructivePillButtonSx, drawerActionButtonSx } from '../../../../theme/componentStyles';

import { Icon } from '@liftango/ui-icons';
interface PeaksEditorProps {
  peaks: Peak[];
  setPeaks: React.Dispatch<React.SetStateAction<Peak[]>>;
  timeLabels: string[];
  defaultPeak: Peak;
  selectedPreset: string;
  onPresetChange: (presetId: string, peaks: Peak[]) => void;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

const PeaksEditor: React.FC<PeaksEditorProps> = ({ 
  peaks, 
  setPeaks, 
  timeLabels, 
  defaultPeak,
  selectedPreset,
  onPresetChange,
  expanded,
  onExpandedChange
}) => {
  const { t } = useTranslation('mapWizards');
  const menuProps = {
    PaperProps: { sx: { zIndex: 2605 } },
    sx: { zIndex: 2605 },
  };
  const preventWheelValueChange = (event: React.WheelEvent<HTMLInputElement>) => {
    event.currentTarget.blur();
  };

  const handlePeakChange = (idx: number, field: keyof Peak, value: string | number) => {
    const newPeaks = peaks.map((p, i) => i === idx ? { ...p, [field]: field === 'peak_time' ? value : Number(value) } : p);
    setPeaks(newPeaks);
    
    // Switch to custom preset when manually editing
    if (selectedPreset !== 'custom') {
      onPresetChange('custom', newPeaks);
    }
  };

  const addPeak = () => {
    if (peaks.length === 0) {
      const newPeaks = [{ ...defaultPeak, peak_time: timeLabels[0] }];
      setPeaks(newPeaks);
      onPresetChange('custom', newPeaks);
      return;
    }
    // Always set the new peak at the last time label (tail end)
    const nextTime = timeLabels[timeLabels.length - 1];
    const newPeaks = [...peaks, { ...defaultPeak, peak_time: nextTime }];
    setPeaks(newPeaks);
    onPresetChange('custom', newPeaks);
  };

  const removePeak = (idx: number) => {
    const newPeaks = peaks.filter((_, i) => i !== idx);
    setPeaks(newPeaks);
    onPresetChange('custom', newPeaks);
  };

  return (
    <Accordion 
      expanded={expanded} 
      onChange={(_, isExpanded) => onExpandedChange(isExpanded)}
      sx={{ 
        mt: 2,
        '&:before': { display: 'none' },
        boxShadow: expanded ? 2 : 1
      }}
    >
      <AccordionSummary
        expandIcon={<Icon size={20} name="chevron-down" />}
        sx={{
          backgroundColor: expanded ? '#f5f5f5' : 'transparent',
          '&:hover': { backgroundColor: '#f9f9f9' }
        }}
      >
        <Typography variant="subtitle1">
          {t('generateRides.peaks.advancedTitle')}
        </Typography>
        {selectedPreset === 'custom' && (
          <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
            {t('generateRides.peaks.customMode')}
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ mt: 1 }}>
          <Button
            variant="contained"
            onClick={addPeak}
            sx={(theme) => ({
              mb: 2,
              ...primaryPillButtonSx(theme),
              ...drawerActionButtonSx,
            })}
          >
            {t('generateRides.peaks.addPeak')}
          </Button>
          {peaks.map((peak, idx) => (
            <Box key={idx} display="flex" alignItems="center" gap={2} mb={1}>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Peak Time</InputLabel>
                <Select
                  label="Peak Time"
                  value={peak.peak_time}
                  MenuProps={menuProps}
                  onChange={e => handlePeakChange(idx, 'peak_time', e.target.value)}
                >
                  {timeLabels.map(t => (
                    <MenuItem key={t} value={t}>{t}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Peak Height"
                type="number"
                value={peak.effect}
                onChange={e => handlePeakChange(idx, 'effect', e.target.value)}
                onWheel={preventWheelValueChange}
                size="small"
                sx={{ width: 120 }}
              />
              <TextField
                label="Width (Left)"
                type="number"
                value={peak.width_left}
                onChange={e => handlePeakChange(idx, 'width_left', e.target.value)}
                onWheel={preventWheelValueChange}
                size="small"
                sx={{ width: 120 }}
              />
              <TextField
                label="Width (Right)"
                type="number"
                value={peak.width_right}
                onChange={e => handlePeakChange(idx, 'width_right', e.target.value)}
                onWheel={preventWheelValueChange}
                size="small"
                sx={{ width: 120 }}
              />
              <Button
                onClick={() => removePeak(idx)}
                size="small"
                sx={(theme) => ({
                  ...destructivePillButtonSx(theme),
                  ...drawerActionButtonSx,
                })}
              >
                Remove
              </Button>
            </Box>
          ))}
          {peaks.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
              No peaks configured. Rides will be distributed evenly throughout the day.
            </Typography>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default PeaksEditor; 
